import './people.css'
import type { IconFields, Pin, StatusKind } from '@feral/shared/client'
import { UserIcon as Icon, type IconState, type RingTier, SMALL_ICON_PX } from '@feral/ui'
import { SpotTag } from '../spots/SpotTag.js'
import { WithWarning } from './marks.js'
import { statusKind, statusWords } from './words.js'

// The icon itself is shared with the console (@feral/ui userIcon.tsx); the app adds your warning.
export { Badge, type IconState, iconWords, SMALL_ICON_PX } from '@feral/ui'

/**
 * A list row's icon (favorites, Who looked, notes, the chain, a spot's people): the reply's icon fields
 * (IconFields, as a pin's), the SFW photo in SFW mode (FR-SFW-02), and your warning on them.
 */
export function rowIcon(
  r: IconFields & { id?: string | undefined; tier: RingTier; eligible?: boolean; photo: string | null },
  sfw: boolean,
): IconState {
  return {
    ring: r.ring,
    tier: r.tier,
    ...(r.eligible ? { eligible: true } : {}),
    photo: sfw ? r.sfw : r.photo,
    status: r.status,
    position: r.position,
    sucking: r.sucking,
    ...(r.id ? { id: r.id } : {}),
  }
}

/**
 * A person, read the same everywhere (docs/USER-ICONS.md): the ring's color is presence and its line the
 * tier; status top left, role top right, position bottom right, your warning bottom left. Under 32 px
 * only the ring and the status. The whole of it is one image to a screen reader, named in words.
 */
export function UserIcon({ s, size = 44 }: { s: IconState; size?: number }) {
  const icon = <Icon s={s} size={size} />
  if (size < SMALL_ICON_PX || !s.id) return icon
  return (
    <WithWarning id={s.id} size={size}>
      {icon}
    </WithWarning>
  )
}

/**
 * A status in words, in its badge's color (IC-R5): "Hosting" orange, "Looking" green. The label alone,
 * or with `long` its time ("Hosting until 1:30"); nothing when browsing or run out (words.ts).
 */
export function StatusText({
  s,
  long,
  className,
}: {
  s: Pin['status'] | StatusKind | null | undefined
  long?: boolean
  className?: string
}) {
  const words = statusWords(s, long)
  if (!words) return null
  // At a spot names it, for those the server tells which (bugs.md): the spot's tag, with its links.
  if (typeof s === 'object' && s?.kind === 'at_spot' && s.spot)
    return <SpotTag spot={s.spot} className={className} />
  return (
    <span className={`fm-status-words fm-status-words--${statusKind(s)}${className ? ` ${className}` : ''}`}>
      {words}
    </span>
  )
}
